import { useMemo } from "react";
import { getStorageUrl, type BucketName } from "@/lib/storage";

/**
 * Hook to convert a storage path to a full URL
 * Handles both paths and legacy full URLs during migration
 */
export function useStorageUrl(
  bucket: BucketName,
  path: string | null | undefined
): string | null {
  return useMemo(() => {
    if (!path) return null;
    // If it's already a full URL (legacy), return as-is
    if (path.startsWith("http")) return path;
    // Otherwise generate URL from path
    return getStorageUrl(bucket, path);
  }, [bucket, path]);
}

/**
 * Hook to convert multiple storage paths to URLs
 * Useful for lists of items with storage paths
 */
export function useStorageUrls<T extends Record<string, any>>(
  items: T[] | null | undefined,
  bucket: BucketName,
  pathKey: keyof T
): (T & { resolvedUrl: string | null })[] {
  return useMemo(() => {
    if (!items) return [];
    return items.map(item => ({
      ...item,
      resolvedUrl: getStorageUrl(bucket, item[pathKey] as string | null)
    }));
  }, [items, bucket, pathKey]);
}
